import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  StyleSheet,
  Pressable,
  View,
  Platform,
  Linking,
  ScrollView,
} from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import PropTypes from 'prop-types';
import { Image } from 'expo-image';

const MAP_HEIGHT = 280;
const MAP_HEIGHT_EXPANDED = 460;
const CARD_WIDTH = 220;
const CARD_GAP = 10;
const SCROLL_STEP = CARD_WIDTH + CARD_GAP;
const isWeb = Platform.OS === 'web';

// Lazy WebView for native only (web uses an iframe).
let webViewResolved = false;
let resolvedWebView = null;
function resolveWebView() {
  if (webViewResolved) return resolvedWebView;
  webViewResolved = true;
  if (isWeb) return null;
  try {
    // eslint-disable-next-line global-require
    const mod = require('react-native-webview');
    resolvedWebView = mod?.WebView || mod?.default || null;
  } catch (_) {
    resolvedWebView = null;
  }
  return resolvedWebView;
}

const IFRAME_STYLE = {
  width: '100%',
  height: '100%',
  border: 0,
  display: 'block',
  backgroundColor: 'transparent',
};

const createStyles = (colors) => StyleSheet.create({
  container: {
    marginTop: 12,
    marginBottom: 4,
    alignSelf: 'stretch',
    overflow: 'hidden',
    maxWidth: '100%',
    // The card carousel is far wider than the chat column. On web, `width: 0`
    // keeps that content out of the parent's max-content calculation (which
    // would otherwise stretch the whole message and stop its text from
    // wrapping), while `minWidth: 100%` still fills the resolved column width.
    ...(isWeb ? { width: 0, minWidth: '100%' } : { width: '100%' }),
  },
  mapCard: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_secondary,
    overflow: 'hidden',
    width: '100%',
    maxWidth: '100%',
  },
  mapWrap: {
    width: '100%',
    backgroundColor: '#e8eef3',
    position: 'relative',
  },
  staticImage: {
    width: '100%',
    height: '100%',
  },
  emptyMap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  detailBar: {
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 8,
  },
  detailHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  pinBadge: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ea580c',
    flexShrink: 0,
  },
  detailTitle: {
    flexShrink: 1,
  },
  detailMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    flexWrap: 'wrap',
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_primary,
    ...(isWeb ? { cursor: 'pointer' } : null),
  },
  actionBtnPrimary: {
    backgroundColor: colors.text_color_primary,
    borderColor: colors.text_color_primary,
  },
  actionSpacer: {
    flexGrow: 1,
  },
  cardsRow: {
    marginTop: 10,
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    position: 'relative',
    // Prevent inherited RTL from parking the carousel at the far end.
    ...(isWeb ? { direction: 'ltr' } : null),
  },
  scrollView: {
    width: '100%',
    maxWidth: '100%',
  },
  scrollContent: {
    flexDirection: 'row',
    flexGrow: 0,
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    gap: CARD_GAP,
    paddingLeft: 2,
    paddingRight: 8,
    paddingVertical: 2,
    ...(isWeb ? { direction: 'ltr' } : null),
  },
  placeCard: {
    width: CARD_WIDTH,
    flexShrink: 0,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_secondary,
    overflow: 'hidden',
    ...(isWeb ? { cursor: 'pointer' } : null),
  },
  placeCardActive: {
    borderColor: '#ea580c',
    ...(isWeb ? { boxShadow: '0 0 0 1px #ea580c' } : null),
  },
  thumbWrap: {
    width: '100%',
    height: 96,
    backgroundColor: colors.solid_color_primary,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  thumb: {
    width: '100%',
    height: '100%',
  },
  cardBadge: {
    position: 'absolute',
    top: 8,
    left: 8,
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(17,24,39,0.85)',
  },
  cardBadgeActive: {
    backgroundColor: '#ea580c',
  },
  placeBody: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    gap: 2,
  },
  ratingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 2,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 2,
    flexWrap: 'wrap',
  },
  openDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#3ecf8e',
  },
  closedDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#f07178',
  },
  navButton: {
    position: 'absolute',
    top: '50%',
    marginTop: -18,
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.solid_color_primary,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    zIndex: 2,
    ...(isWeb ? {
      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
      cursor: 'pointer',
    } : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.15,
      shadowRadius: 4,
      elevation: 3,
    }),
  },
  navButtonLeft: {
    left: 4,
  },
  navButtonRight: {
    right: 4,
  },
});

const NavButton = React.memo(({ direction, onPress, colors, styles }) => (
  <Pressable
    onPress={onPress}
    style={[
      styles.navButton,
      direction === 'left' ? styles.navButtonLeft : styles.navButtonRight,
    ]}
    accessibilityRole="button"
    accessibilityLabel={direction === 'left' ? 'Previous places' : 'Next places'}
  >
    <Icon
      name={direction === 'left' ? 'chevron-left' : 'chevron-right'}
      size={14}
      color={colors.text_color_primary}
    />
  </Pressable>
));
NavButton.displayName = 'PlacesNavButton';

function directionsUrl(place) {
  if (!place) return null;
  const destination = place.gps && typeof place.gps.lat === 'number'
    ? `${place.gps.lat},${place.gps.lng}`
    : [place.title, place.address].filter(Boolean).join(' ');
  if (!destination) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
}

function mapsUrl(place) {
  if (!place) return null;
  const link = normalizeUrl(place.link);
  if (link) return link;
  if (place.gps && typeof place.gps.lat === 'number') {
    return `https://www.google.com/maps/search/?api=1&query=${place.gps.lat},${place.gps.lng}`;
  }
  const label = [place.title, place.address].filter(Boolean).join(' ');
  return label ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(label)}` : null;
}

function buildMapHtml(places, showPopups = true) {
  const pins = (places || [])
    .map((p, i) => ({
      lat: p?.gps?.lat,
      lng: p?.gps?.lng,
      title: p?.title || 'Place',
      address: p?.address || '',
      type: p?.type || '',
      rating: typeof p?.rating === 'number' ? p.rating : null,
      reviews: typeof p?.reviews === 'number' ? p.reviews : null,
      openState: p?.openState || '',
      price: p?.price || '',
      phone: p?.phone || '',
      website: normalizeUrl(p?.website) || '',
      directions: directionsUrl(p) || '',
      maps: mapsUrl(p) || '',
      index: i,
      hasGps: !!(p?.gps && typeof p.gps.lat === 'number' && typeof p.gps.lng === 'number'),
    }))
    .filter(p => p.hasGps);

  const center = pins[0] ? { lat: pins[0].lat, lng: pins[0].lng } : { lat: 39.95, lng: -75.16 };

  // A place name containing "</script>" would otherwise close the tag early.
  const embed = (value) => JSON.stringify(value).replace(/</g, '\\u003c');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
  <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
  <style>
    html, body, #map { margin: 0; padding: 0; width: 100%; height: 100%; background: #e8eef3; }
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }
    .leaflet-control-attribution { font-size: 10px !important; background: rgba(255,255,255,0.75) !important; color: #555 !important; }
    .leaflet-control-attribution a { color: #333 !important; }

    /* Leaflet's own zoom control: anchored inside the map pane so it can never
       be pushed outside the visible frame. */
    .leaflet-touch .leaflet-bar, .leaflet-bar {
      border: 1px solid rgba(0,0,0,0.12) !important;
      border-radius: 10px !important;
      box-shadow: 0 2px 10px rgba(0,0,0,0.18) !important;
      overflow: hidden;
    }
    .leaflet-bar a, .leaflet-touch .leaflet-bar a {
      width: 34px !important; height: 34px !important;
      line-height: 34px !important;
      font-size: 20px !important; font-weight: 700;
      color: #111827 !important; background: rgba(255,255,255,0.97) !important;
    }
    .leaflet-bar a:hover { background: #fff !important; }
    .leaflet-bar a:first-child { border-bottom: 1px solid rgba(0,0,0,0.08) !important; }
    @media (pointer: coarse) {
      .leaflet-bar a, .leaflet-touch .leaflet-bar a {
        width: 44px !important; height: 44px !important; line-height: 44px !important;
      }
    }

    .pin {
      width: 28px; height: 28px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      color: #fff; font: 700 12px/1 -apple-system, BlinkMacSystemFont, sans-serif;
      border: 2px solid #fff; box-shadow: 0 2px 8px rgba(0,0,0,0.35);
      background: #2563eb; cursor: pointer;
      transition: transform 120ms ease, background 120ms ease;
    }
    .pin.selected { background: #ea580c; transform: scale(1.2); z-index: 10; }

    .leaflet-popup-content-wrapper { border-radius: 12px; box-shadow: 0 6px 24px rgba(0,0,0,0.22); }
    .leaflet-popup-content { margin: 10px 12px; width: 210px !important; }
    .pop-title { font: 700 13px/1.3 -apple-system, sans-serif; color: #111827; }
    .pop-meta { font: 400 11px/1.4 -apple-system, sans-serif; color: #6b7280; margin-top: 3px; }
    .pop-meta.addr { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .pop-rating { color: #b45309; font-weight: 600; }
    .pop-open { color: #047857; font-weight: 600; }
    .pop-closed { color: #b91c1c; font-weight: 600; }
    .pop-actions { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
    .pop-btn {
      appearance: none; border: 1px solid rgba(0,0,0,0.12); background: #f9fafb;
      border-radius: 999px; padding: 5px 10px; cursor: pointer;
      font: 600 11px/1 -apple-system, sans-serif; color: #111827;
    }
    .pop-btn.primary { background: #111827; border-color: #111827; color: #fff; }
    .pop-btn:hover { filter: brightness(0.96); }
  </style>
</head>
<body>
  <div id="map"></div>
  <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
  <script>
    var pins = ${embed(pins)};
    var center = ${embed(center)};
    var selectedIndex = 0;
    var markersByIndex = {};
    var container = document.getElementById('map');
    // Once the viewer drives the map, auto-framing must stop fighting them.
    var userMoved = false;
    function markUserMoved() { userMoved = true; }

    var map = L.map('map', {
      zoomControl: true,
      attributionControl: true,
      scrollWheelZoom: false,
      tap: true,
    });
    map.zoomControl.setPosition('topleft');
    container.addEventListener('pointerdown', markUserMoved, true);
    container.addEventListener('touchstart', markUserMoved, { capture: true, passive: true });
    // A bare wheel should scroll the conversation, not the map. Ctrl/Cmd+wheel
    // is the escape hatch for zooming without leaving the keyboard.
    container.addEventListener('wheel', function (e) {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      markUserMoved();
      map.setZoom(map.getZoom() + (e.deltaY < 0 ? 1 : -1));
    }, { passive: false });

    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap'
    }).addTo(map);

    function post(payload) {
      var raw = JSON.stringify(payload);
      if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
        window.ReactNativeWebView.postMessage(raw);
      } else if (window.parent && window.parent !== window) {
        window.parent.postMessage(raw, '*');
      }
    }

    function esc(value) {
      return String(value == null ? '' : value)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    function makeIcon(p, selected) {
      return L.divIcon({
        className: '',
        html: '<div class="pin' + (selected ? ' selected' : '') + '">' + (p.index + 1) + '</div>',
        iconSize: [28, 28],
        iconAnchor: [14, 14],
        popupAnchor: [0, -16],
      });
    }

    function popupHtml(p) {
      var bits = [];
      if (typeof p.rating === 'number') {
        bits.push('<span class="pop-rating">' + p.rating.toFixed(1) + ' ★</span>'
          + (p.reviews ? ' (' + p.reviews + ')' : ''));
      }
      if (p.type) bits.push(esc(p.type));
      if (p.price) bits.push(esc(p.price));

      var html = '<div class="pop-title">' + esc(p.title) + '</div>';
      if (bits.length) html += '<div class="pop-meta">' + bits.join(' &middot; ') + '</div>';
      if (p.openState) {
        var cls = /^open/i.test(p.openState) ? 'pop-open' : (/^closed/i.test(p.openState) ? 'pop-closed' : '');
        html += '<div class="pop-meta"><span class="' + cls + '">' + esc(p.openState) + '</span></div>';
      }
      if (p.address) html += '<div class="pop-meta addr" title="' + esc(p.address) + '">' + esc(p.address) + '</div>';

      var actions = '';
      if (p.directions) actions += '<button class="pop-btn primary" data-open="' + esc(p.directions) + '">Directions</button>';
      if (p.phone) actions += '<button class="pop-btn" data-open="tel:' + esc(p.phone.replace(/[^0-9+]/g, '')) + '">Call</button>';
      if (p.website) actions += '<button class="pop-btn" data-open="' + esc(p.website) + '">Website</button>';
      if (!p.directions && p.maps) actions += '<button class="pop-btn" data-open="' + esc(p.maps) + '">Open in Maps</button>';
      if (actions) html += '<div class="pop-actions">' + actions + '</div>';
      return html;
    }

    function setSelected(index, opts) {
      opts = opts || {};
      var previous = selectedIndex;
      selectedIndex = index;
      // Only touch the two markers whose state changed — re-icon'ing every
      // marker makes an open popup flicker.
      if (previous !== index) {
        [previous, index].forEach(function (i) {
          var marker = markersByIndex[i];
          var pin = pins.find(function (p) { return p.index === i; });
          if (marker && pin) marker.setIcon(makeIcon(pin, i === selectedIndex));
        });
      }
      var target = markersByIndex[index];
      if (!target) return;
      if (opts.pan) {
        map.panTo(target.getLatLng(), { animate: true, duration: 0.35 });
      }
      if (opts.openPopup && ${showPopups}) {
        target.openPopup();
      }
    }

    function pinBounds() {
      if (!pins.length) return null;
      var bounds = L.latLngBounds(pins.map(function (p) { return L.latLng(p.lat, p.lng); }));
      return bounds.isValid() ? bounds : null;
    }

    function fitAllPins(attempt) {
      attempt = attempt || 0;
      // Leaflet caches container size, so refresh it before measuring: fitting
      // against a stale (or zero) size parks the results outside the frame.
      map.invalidateSize({ animate: false, pan: false });
      var size = map.getSize();
      if (!size || size.x < 200 || size.y < 140) {
        if (attempt < 30) setTimeout(function () { fitAllPins(attempt + 1); }, 100);
        return;
      }
      if (pins.length === 1) { map.setView([pins[0].lat, pins[0].lng], 14); return; }
      var bounds = pinBounds();
      if (!bounds) { map.setView([center.lat, center.lng], 12); return; }
      map.fitBounds(bounds, { padding: [36, 36], maxZoom: 15, animate: false });
    }

    // Size heuristics kept getting this wrong, so verify the thing that
    // actually matters: are all the results inside the current viewport?
    function pinsFramed() {
      var bounds = pinBounds();
      if (!bounds) return true;
      var view = map.getBounds();
      if (!view || !view.isValid()) return false;
      return pins.length === 1 ? view.contains(bounds.getCenter()) : view.contains(bounds);
    }

    function ensureFramed() {
      if (userMoved) return;
      map.invalidateSize({ animate: false, pan: false });
      var size = map.getSize();
      if (!size || size.x < 200 || size.y < 140) return;
      if (pinsFramed()) return;
      fitAllPins(0);
    }

    pins.forEach(function (p) {
      var marker = L.marker([p.lat, p.lng], {
        icon: makeIcon(p, p.index === 0),
        riseOnHover: true,
        title: p.title,
      }).addTo(map);
      if (${showPopups}) marker.bindPopup(popupHtml(p), {
        closeButton: true,
        autoPan: true,
        autoPanPadding: [16, 16],
        maxWidth: 240,
      });
      marker.on('click', function () {
        setSelected(p.index, { pan: true, openPopup: true });
        post({ type: 'select', index: p.index });
      });
      markersByIndex[p.index] = marker;
    });

    // Popup buttons cannot open windows from a sandboxed frame; hand the URL to
    // the host app instead.
    document.addEventListener('click', function (event) {
      var btn = event.target && event.target.closest && event.target.closest('[data-open]');
      if (!btn) return;
      event.preventDefault();
      event.stopPropagation();
      post({ type: 'open', url: btn.getAttribute('data-open') });
    });

    map.setView([center.lat, center.lng], 12);
    map.whenReady(function () { fitAllPins(0); });
    window.addEventListener('load', function () { fitAllPins(0); });

    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(function () { ensureFramed(); }).observe(container);
    }

    // The frame can settle late (fonts, layout passes, the parent applying its
    // final width). Re-check until it holds still or the user takes over.
    var watchdog = setInterval(function () {
      if (userMoved) { clearInterval(watchdog); return; }
      ensureFramed();
    }, 300);
    setTimeout(function () { clearInterval(watchdog); }, 8000);

    window.addEventListener('message', function (event) {
      try {
        var data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (!data) return;
        if (data.type === 'selectPin' && typeof data.index === 'number') {
          setSelected(data.index, {});
        } else if (data.type === 'focusPin' && typeof data.index === 'number') {
          markUserMoved();
          setSelected(data.index, { pan: true, openPopup: true });
        } else if (data.type === 'zoomIn') {
          markUserMoved();
          map.zoomIn();
        } else if (data.type === 'zoomOut') {
          markUserMoved();
          map.zoomOut();
        } else if (data.type === 'refit') {
          // An explicit refit (load, expand/collapse) re-takes control.
          userMoved = false;
          fitAllPins(0);
        }
      } catch (_) {}
    });
  </script>
</body>
</html>`;
}

const SAFE_URL = /^(https?:|tel:|mailto:)/i;

/** SerpAPI sometimes returns bare hostnames for `website`. */
function normalizeUrl(url) {
  if (!url) return null;
  const trimmed = String(url).trim();
  if (!trimmed) return null;
  if (SAFE_URL.test(trimmed)) return trimmed;
  if (/^[\w.-]+\.[a-z]{2,}(\/|$)/i.test(trimmed)) return `https://${trimmed}`;
  return null;
}

function openLink(url) {
  if (!url || !SAFE_URL.test(String(url))) return;
  if (isWeb && typeof window !== 'undefined') {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  Linking.openURL(url).catch(() => {});
}

function useThumbnail(place) {
  const candidates = useMemo(() => {
    const list = [];
    if (place?.thumbnail) list.push(place.thumbnail);
    if (Array.isArray(place?.thumbnails)) {
      for (const url of place.thumbnails) {
        if (url && !list.includes(url)) list.push(url);
      }
    }
    // Prefer SerpAPI-proxied images — raw googleusercontent often paints blank.
    return list.sort((a, b) => {
      const aSerp = a.includes('serpapi.com') ? 0 : 1;
      const bSerp = b.includes('serpapi.com') ? 0 : 1;
      return aSerp - bSerp;
    });
  }, [place?.thumbnail, place?.thumbnails]);

  const [index, setIndex] = useState(0);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    setIndex(0);
    setHidden(false);
  }, [place?._id, place?.placeId, place?.thumbnail]);

  const onError = useCallback(() => {
    if (index + 1 < candidates.length) {
      setIndex(index + 1);
      return;
    }
    setHidden(true);
  }, [index, candidates.length]);

  return { uri: hidden ? null : candidates[index], onError };
}

const ActionButton = React.memo(({ icon, label, onPress, primary, colors, styles }) => (
  <Pressable
    onPress={onPress}
    style={[styles.actionBtn, primary && styles.actionBtnPrimary]}
    accessibilityRole="button"
    accessibilityLabel={label}
  >
    <Icon
      name={icon}
      size={11}
      color={primary ? colors.solid_color_primary : colors.text_color_primary}
    />
    <Text size="xs" weight="bold" style={primary ? { color: colors.solid_color_primary } : null}>
      {label}
    </Text>
  </Pressable>
));
ActionButton.displayName = 'PlacesActionButton';

function PlaceCard({ place, index, active, onPress, styles, colors }) {
  const isOpen = typeof place.openState === 'string' && /^open\b/i.test(place.openState);
  const isClosed = typeof place.openState === 'string' && /^closed\b/i.test(place.openState);
  const thumb = useThumbnail(place);

  return (
    <Pressable
      onPress={onPress}
      style={[styles.placeCard, active && styles.placeCardActive]}
      accessibilityRole="button"
      accessibilityLabel={place.title || 'Place'}
    >
      <View style={styles.thumbWrap}>
        {thumb.uri ? (
          <Image
            source={{ uri: thumb.uri }}
            style={styles.thumb}
            contentFit="cover"
            onError={thumb.onError}
          />
        ) : (
          <Icon name="location-dot" size={20} color={colors.text_color_tertiary} />
        )}
        <View style={[styles.cardBadge, active && styles.cardBadgeActive]}>
          <Text size="xxs" weight="extraBold" color="white">{index + 1}</Text>
        </View>
      </View>
      <View style={styles.placeBody}>
        <Text size="s" weight="bold" numberOfLines={2}>
          {place.title}
        </Text>
        {(typeof place.rating === 'number' || place.type) && (
          <View style={styles.ratingRow}>
            {typeof place.rating === 'number' && (
              <>
                <Icon name="star" size={11} color="#fbbf24" solid />
                <Text size="xs" color="secondary">{place.rating.toFixed(1)}</Text>
                {typeof place.reviews === 'number' && (
                  <Text size="xs" color="tertiary">({place.reviews})</Text>
                )}
              </>
            )}
            {place.type ? (
              <Text size="xs" color="tertiary" numberOfLines={1}>
                {typeof place.rating === 'number' ? ' · ' : ''}{place.type}
              </Text>
            ) : null}
          </View>
        )}
        {(place.openState || place.address) && (
          <View style={styles.metaRow}>
            {place.openState ? (
              <>
                {(isOpen || isClosed) ? (
                  <View style={isOpen ? styles.openDot : styles.closedDot} />
                ) : null}
                <Text size="xs" color="secondary" numberOfLines={1}>{place.openState}</Text>
              </>
            ) : null}
            {place.address ? (
              <Text size="xs" color="tertiary" numberOfLines={1} style={{ flexShrink: 1 }}>
                {place.openState ? ' · ' : ''}{place.address}
              </Text>
            ) : null}
          </View>
        )}
      </View>
    </Pressable>
  );
}

function MessagePlaces({ places = [], map = null, presentation = 'chat', onPlacePress, activePlace }) {
  const mapOnly = presentation === 'map-only';
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const iframeRef = useRef(null);
  const webViewRef = useRef(null);
  const cardsScrollRef = useRef(null);
  const nativeOffsetRef = useRef(0);
  const nativeViewportRef = useRef(0);
  const nativeContentRef = useRef(0);
  const mapLayoutWidthRef = useRef(0);

  const mappablePlaces = useMemo(
    () => (places || []).filter(p => p?.gps && typeof p.gps.lat === 'number' && typeof p.gps.lng === 'number'),
    [places]
  );

  // Stable HTML — do NOT remount on selection/expand (that resets the viewport).
  const html = useMemo(() => buildMapHtml(places, !mapOnly), [places, mapOnly]);

  const NativeWebView = resolveWebView();
  const canShowInteractive = mappablePlaces.length > 0 && (isWeb || !!NativeWebView);
  const staticImage = map?.staticImage || null;
  const mapHeight = expanded ? MAP_HEIGHT_EXPANDED : presentation === 'discovery' ? 260 : MAP_HEIGHT;
  const selectedPlace = places[selectedIndex] || places[0] || null;
  useEffect(() => {
    const index = places.indexOf(activePlace);
    if (index >= 0) setSelectedIndex(index);
  }, [activePlace, places]);

  const postToMap = useCallback((payload) => {
    const raw = JSON.stringify(payload);
    if (isWeb) {
      iframeRef.current?.contentWindow?.postMessage(raw, '*');
      return;
    }
    webViewRef.current?.injectJavaScript?.(
      `window.dispatchEvent(new MessageEvent('message', { data: ${JSON.stringify(raw)} })); true;`
    );
  }, []);

  const requestMapRefit = useCallback(() => {
    postToMap({ type: 'refit' });
    setTimeout(() => postToMap({ type: 'refit' }), 120);
    setTimeout(() => postToMap({ type: 'refit' }), 350);
  }, [postToMap]);

  // The chat column settles its width after mount; re-frame when it does.
  const onMapLayout = useCallback((event) => {
    const width = Math.round(event?.nativeEvent?.layout?.width || 0);
    if (!width || width === mapLayoutWidthRef.current) return;
    mapLayoutWidthRef.current = width;
    postToMap({ type: 'refit' });
  }, [postToMap]);

  const scrollCardIntoView = useCallback((index) => {
    const target = Math.max(0, index * SCROLL_STEP - SCROLL_STEP / 2);
    if (isWeb) {
      cardsScrollRef.current?.scrollTo?.({ left: target, behavior: 'smooth' });
      return;
    }
    cardsScrollRef.current?.scrollTo?.({ x: target, animated: true });
    nativeOffsetRef.current = target;
  }, []);

  const handleMessage = useCallback((event) => {
    try {
      const raw = event?.nativeEvent?.data || event?.data;
      const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (data?.type === 'select' && Number.isInteger(data.index) && places[data.index]) {
        setSelectedIndex(data.index);
        scrollCardIntoView(data.index);
        onPlacePress?.(places[data.index]);
      } else if (data?.type === 'open' && data.url) {
        openLink(data.url);
      }
    } catch (_) {
      // ignore malformed messages
    }
  }, [scrollCardIntoView, places, onPlacePress]);

  useEffect(() => {
    if (!isWeb || !canShowInteractive) return undefined;
    const onWindowMessage = (event) => {
      if (event?.source !== iframeRef.current?.contentWindow) return;
      handleMessage({ data: event?.data });
    };
    window.addEventListener('message', onWindowMessage);
    return () => window.removeEventListener('message', onWindowMessage);
  }, [canShowInteractive, handleMessage]);

  // Keep the pin highlight in sync without remounting the map.
  useEffect(() => {
    if (!canShowInteractive) return;
    postToMap({ type: 'selectPin', index: selectedIndex });
  }, [selectedIndex, canShowInteractive, postToMap]);

  // After an expand/collapse height change, Leaflet needs to remeasure.
  useEffect(() => {
    if (!canShowInteractive) return undefined;
    const t1 = setTimeout(() => postToMap({ type: 'refit' }), 80);
    const t2 = setTimeout(() => postToMap({ type: 'refit' }), 300);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [expanded, canShowInteractive, postToMap]);

  const updateScrollState = useCallback((scrollLeft, clientWidth, scrollWidth) => {
    setCanScrollLeft(scrollLeft > 2);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 2);
  }, []);

  const syncFromWebEl = useCallback(() => {
    const el = cardsScrollRef.current;
    if (!el) return;
    updateScrollState(el.scrollLeft, el.clientWidth, el.scrollWidth);
  }, [updateScrollState]);

  useEffect(() => {
    if (!isWeb) return undefined;
    const id = requestAnimationFrame(() => {
      const el = cardsScrollRef.current;
      if (!el) return;
      el.scrollTo?.({ left: 0, top: 0 });
      if ('scrollLeft' in el) el.scrollLeft = 0;
      syncFromWebEl();
    });
    const el = cardsScrollRef.current;
    if (!el) return () => cancelAnimationFrame(id);
    const onScroll = () => syncFromWebEl();
    el.addEventListener('scroll', onScroll, { passive: true });
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(syncFromWebEl);
      ro.observe(el);
    }
    return () => {
      cancelAnimationFrame(id);
      el.removeEventListener('scroll', onScroll);
      ro?.disconnect();
    };
  }, [places, syncFromWebEl]);

  useEffect(() => {
    if (isWeb) return undefined;
    const id = requestAnimationFrame(() => {
      cardsScrollRef.current?.scrollTo?.({ x: 0, y: 0, animated: false });
    });
    return () => cancelAnimationFrame(id);
  }, [places]);

  const syncNativeState = useCallback(() => {
    updateScrollState(
      nativeOffsetRef.current,
      nativeViewportRef.current,
      nativeContentRef.current,
    );
  }, [updateScrollState]);

  const onNativeScroll = useCallback((event) => {
    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
    nativeOffsetRef.current = contentOffset.x;
    nativeViewportRef.current = layoutMeasurement.width;
    nativeContentRef.current = contentSize.width;
    updateScrollState(contentOffset.x, layoutMeasurement.width, contentSize.width);
  }, [updateScrollState]);

  const scrollBy = useCallback((delta) => {
    if (isWeb) {
      const el = cardsScrollRef.current;
      if (!el) return;
      el.scrollBy?.({ left: delta, behavior: 'smooth' });
      requestAnimationFrame(syncFromWebEl);
      return;
    }
    const maxOffset = Math.max(0, nativeContentRef.current - nativeViewportRef.current);
    const next = Math.min(maxOffset, Math.max(0, nativeOffsetRef.current + delta));
    cardsScrollRef.current?.scrollTo?.({ x: next, animated: true });
    nativeOffsetRef.current = next;
    syncNativeState();
  }, [syncFromWebEl, syncNativeState]);

  const selectPlace = useCallback((index) => {
    setSelectedIndex(index);
    postToMap({ type: 'focusPin', index });
    onPlacePress?.(places[index]);
  }, [postToMap, places, onPlacePress]);

  if (!Array.isArray(places) || places.length === 0) return null;

  const isOpenNow = typeof selectedPlace?.openState === 'string' && /^open\b/i.test(selectedPlace.openState);
  const isClosedNow = typeof selectedPlace?.openState === 'string' && /^closed\b/i.test(selectedPlace.openState);

  return (
    <View style={[styles.container, mapOnly && { flex: 1, marginTop: 0, marginBottom: 0 }]}>
      <View style={[styles.mapCard, mapOnly && { flex: 1 }]}>
        <View style={[styles.mapWrap, mapOnly ? { flex: 1 } : { height: mapHeight }]} onLayout={onMapLayout}>
          {canShowInteractive && isWeb ? (
            <iframe
              ref={iframeRef}
              title="Places map"
              srcDoc={html}
              style={IFRAME_STYLE}
              sandbox="allow-scripts allow-same-origin"
              onLoad={requestMapRefit}
            />
          ) : canShowInteractive && NativeWebView ? (
            <NativeWebView
              ref={webViewRef}
              originWhitelist={['*']}
              source={{ html }}
              style={{ flex: 1, backgroundColor: 'transparent' }}
              onMessage={handleMessage}
              onLoadEnd={requestMapRefit}
              scrollEnabled={false}
              nestedScrollEnabled
              javaScriptEnabled
              domStorageEnabled
              setSupportMultipleWindows={false}
              {...(Platform.OS === 'android' ? { mixedContentMode: 'always' } : null)}
            />
          ) : staticImage ? (
            <Pressable onPress={() => openLink(mapsUrl(selectedPlace))} style={{ flex: 1 }}>
              <Image source={{ uri: staticImage }} style={styles.staticImage} contentFit="cover" />
            </Pressable>
          ) : (
            <Pressable onPress={() => openLink(mapsUrl(selectedPlace))} style={styles.emptyMap}>
              <Icon name="map" size={28} color={colors.text_color_secondary} />
              <Text size="s" color="secondary" style={{ marginTop: 8 }}>
                Open in Maps
              </Text>
            </Pressable>
          )}
        </View>

        {selectedPlace && !mapOnly ? (
          <View style={styles.detailBar}>
            <View style={styles.detailHeader}>
              <View style={styles.pinBadge}>
                <Text size="xxs" weight="extraBold" color="white">{selectedIndex + 1}</Text>
              </View>
              <Text size="s" weight="bold" numberOfLines={1} style={styles.detailTitle}>
                {selectedPlace.title}
              </Text>
            </View>
            <View style={styles.detailMeta}>
              {typeof selectedPlace.rating === 'number' ? (
                <>
                  <Icon name="star" size={10} color="#fbbf24" solid />
                  <Text size="xs" color="secondary">
                    {selectedPlace.rating.toFixed(1)}
                    {typeof selectedPlace.reviews === 'number' ? ` (${selectedPlace.reviews})` : ''}
                  </Text>
                </>
              ) : null}
              {selectedPlace.openState ? (
                <>
                  {(isOpenNow || isClosedNow) ? (
                    <View style={isOpenNow ? styles.openDot : styles.closedDot} />
                  ) : null}
                  <Text size="xs" color="secondary" numberOfLines={1}>{selectedPlace.openState}</Text>
                </>
              ) : null}
              {selectedPlace.address ? (
                <Text size="xs" color="tertiary" numberOfLines={1} style={{ flexShrink: 1 }}>
                  · {selectedPlace.address}
                </Text>
              ) : null}
            </View>
            <View style={styles.actionRow}>
              {directionsUrl(selectedPlace) ? (
                <ActionButton
                  icon="route"
                  label="Directions"
                  primary
                  onPress={() => openLink(directionsUrl(selectedPlace))}
                  colors={colors}
                  styles={styles}
                />
              ) : null}
              {selectedPlace.phone ? (
                <ActionButton
                  icon="phone"
                  label="Call"
                  onPress={() => openLink(`tel:${String(selectedPlace.phone).replace(/[^0-9+]/g, '')}`)}
                  colors={colors}
                  styles={styles}
                />
              ) : null}
              {normalizeUrl(selectedPlace.website) ? (
                <ActionButton
                  icon="globe"
                  label="Website"
                  onPress={() => openLink(normalizeUrl(selectedPlace.website))}
                  colors={colors}
                  styles={styles}
                />
              ) : null}
              {mapsUrl(selectedPlace) ? (
                <ActionButton
                  icon="arrow-up-right-from-square"
                  label="Maps"
                  onPress={() => openLink(mapsUrl(selectedPlace))}
                  colors={colors}
                  styles={styles}
                />
              ) : null}
              {canShowInteractive ? (
                <>
                  <View style={styles.actionSpacer} />
                  <ActionButton
                    icon={expanded ? 'compress' : 'expand'}
                    label={expanded ? 'Collapse' : 'Expand'}
                    onPress={() => setExpanded(v => !v)}
                    colors={colors}
                    styles={styles}
                  />
                </>
              ) : null}
            </View>
          </View>
        ) : null}
      </View>

      {!mapOnly && <View style={styles.cardsRow}>
        <ScrollView
          ref={cardsScrollRef}
          horizontal
          style={styles.scrollView}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.scrollContent}
          contentOffset={{ x: 0, y: 0 }}
          onScroll={!isWeb ? onNativeScroll : undefined}
          scrollEventThrottle={16}
          onContentSizeChange={!isWeb ? (w) => {
            nativeContentRef.current = w;
            syncNativeState();
          } : undefined}
          onLayout={!isWeb ? (e) => {
            nativeViewportRef.current = e.nativeEvent.layout.width;
            syncNativeState();
          } : undefined}
          {...(isWeb ? { dir: 'ltr' } : null)}
        >
          {places.map((place, index) => (
            <PlaceCard
              key={place._id || place.placeId || `${place.title}-${index}`}
              place={place}
              index={index}
              active={index === selectedIndex}
              onPress={() => selectPlace(index)}
              styles={styles}
              colors={colors}
            />
          ))}
        </ScrollView>
        {canScrollLeft ? (
          <NavButton direction="left" onPress={() => scrollBy(-SCROLL_STEP)} colors={colors} styles={styles} />
        ) : null}
        {canScrollRight ? (
          <NavButton direction="right" onPress={() => scrollBy(SCROLL_STEP)} colors={colors} styles={styles} />
        ) : null}
      </View>}
    </View>
  );
}

MessagePlaces.propTypes = {
  onPlacePress: PropTypes.func,
  activePlace: PropTypes.object,
  presentation: PropTypes.oneOf(['chat', 'discovery', 'map-only']),
  places: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string,
    title: PropTypes.string,
    address: PropTypes.string,
    rating: PropTypes.number,
    reviews: PropTypes.number,
    type: PropTypes.string,
    phone: PropTypes.string,
    website: PropTypes.string,
    openState: PropTypes.string,
    hours: PropTypes.string,
    price: PropTypes.string,
    thumbnail: PropTypes.string,
    thumbnails: PropTypes.arrayOf(PropTypes.string),
    link: PropTypes.string,
    placeId: PropTypes.string,
    position: PropTypes.number,
    gps: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    }),
    isReference: PropTypes.bool,
  })),
  map: PropTypes.shape({
    center: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    }),
    zoom: PropTypes.number,
    staticImage: PropTypes.string,
    query: PropTypes.string,
  }),
};

export { useThumbnail as usePlaceThumbnail, mapsUrl as placeMapsUrl, openLink as openPlaceLink };
export default MessagePlaces;
